import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import { Button } from 'gatorcomponents';
import * as Styles from './ImageCropper.styles';

const ImageCropper = ({
  imageSrc,
  onCropComplete,
  onCancel,
  locale,
  isSaving = false,
}) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const [imageTransform, setImageTransform] = useState({
    x: 0,
    y: 0,
    scale: 1,
  });

  const [cropArea, setCropArea] = useState({
    x: 0,
    y: 0,
    size: 300,
  });

  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isDraggingCorner, setIsDraggingCorner] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;

      const onLoad = () => {
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        setImageNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });

        const scaleX = containerWidth / img.naturalWidth;
        const scaleY = containerHeight / img.naturalHeight;
        const initialScale = Math.max(scaleX, scaleY, 0.5);

        const scaledWidth = img.naturalWidth * initialScale;
        const scaledHeight = img.naturalHeight * initialScale;

        setImageTransform({
          x: (containerWidth - scaledWidth) / 2,
          y: (containerHeight - scaledHeight) / 2,
          scale: initialScale,
        });

        const cropSize = Math.min(containerWidth, containerHeight) * 0.5;
        setCropArea({
          x: (containerWidth - cropSize) / 2,
          y: (containerHeight - cropSize) / 2,
          size: cropSize,
        });
      };

      if (img.complete) {
        onLoad();
      } else {
        img.addEventListener('load', onLoad);
        return () => img.removeEventListener('load', onLoad);
      }
    }
  }, [imageSrc]);

  const handleImageMouseDown = useCallback((e) => {
    if (isDraggingCorner) return;
    setIsDraggingImage(true);
    setDragStart({
      x: e.clientX - imageTransform.x,
      y: e.clientY - imageTransform.y,
    });
  }, [imageTransform, isDraggingCorner]);

  const handleMouseMove = useCallback((e) => {
    if (isDraggingImage) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setImageTransform(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isDraggingCorner && containerRef.current) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const centerX = cropArea.x + cropArea.size / 2;
      const centerY = cropArea.y + cropArea.size / 2;

      const distanceX = mouseX - centerX;
      const distanceY = mouseY - centerY;
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

      const newSize = Math.max(50, Math.min(distance * 2, Math.min(rect.width, rect.height) * 0.9));

      setCropArea({
        x: centerX - newSize / 2,
        y: centerY - newSize / 2,
        size: newSize,
      });
    }
  }, [isDraggingImage, isDraggingCorner, dragStart, cropArea]);

  const handleMouseUp = useCallback(() => {
    setIsDraggingImage(false);
    setIsDraggingCorner(null);
  }, []);

  const handleCornerMouseDown = useCallback((corner, e) => {
    e.stopPropagation();
    setIsDraggingCorner(corner);
  }, []);

  const handleImageTouchStart = useCallback((e) => {
    if (isDraggingCorner) return;
    const touch = e.touches[0];
    setIsDraggingImage(true);
    setDragStart({
      x: touch.clientX - imageTransform.x,
      y: touch.clientY - imageTransform.y,
    });
  }, [imageTransform, isDraggingCorner]);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    if (isDraggingImage) {
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      setImageTransform(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isDraggingCorner && containerRef.current) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;

      const centerX = cropArea.x + cropArea.size / 2;
      const centerY = cropArea.y + cropArea.size / 2;

      const distanceX = touchX - centerX;
      const distanceY = touchY - centerY;
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

      const newSize = Math.max(50, Math.min(distance * 2, Math.min(rect.width, rect.height) * 0.9));

      setCropArea({
        x: centerX - newSize / 2,
        y: centerY - newSize / 2,
        size: newSize,
      });
    }
  }, [isDraggingImage, isDraggingCorner, dragStart, cropArea]);

  const handleTouchEnd = useCallback(() => {
    setIsDraggingImage(false);
    setIsDraggingCorner(null);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const getCroppedImage = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;

    const scaleRatio = imageNaturalSize.width / (imageRef.current.naturalWidth * imageTransform.scale);

    const cropX = (cropArea.x - imageTransform.x) * scaleRatio;
    const cropY = (cropArea.y - imageTransform.y) * scaleRatio;
    const cropSize = cropArea.size * scaleRatio;

    ctx.save();
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    ctx.drawImage(
      imageRef.current,
      cropX,
      cropY,
      cropSize,
      cropSize,
      0,
      0,
      outputSize,
      outputSize,
    );

    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.95);
  }, [cropArea, imageTransform, imageNaturalSize]);

  const handleConfirm = () => {
    const croppedImage = getCroppedImage();
    if (croppedImage) {
      onCropComplete(croppedImage);
    }
  };

  const handleContainerClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Styles.Container onClick={handleContainerClick}>
      <Styles.Title>{locale.cropTitle}</Styles.Title>
      <Styles.Description>
        {locale.cropDescription}
      </Styles.Description>

      <Styles.CropContainer
        ref={containerRef}
        onMouseDown={handleImageMouseDown}
        onTouchStart={handleImageTouchStart}
        onClick={e => e.stopPropagation()}
      >
        <Styles.Image
          ref={imageRef}
          src={imageSrc}
          alt="Crop"
          $transform={`translate(${imageTransform.x}px, ${imageTransform.y}px) scale(${imageTransform.scale})`}
          $isDragging={isDraggingImage}
          draggable={false}
        />

        <Styles.CropCircle
          $left={cropArea.x}
          $top={cropArea.y}
          $size={cropArea.size}
        >
          <Styles.CornerHandle
            position="top-left"
            cursor="nwse-resize"
            onMouseDown={e => handleCornerMouseDown('top-left', e)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDraggingCorner('top-left');
            }}
          />
          <Styles.CornerHandle
            position="top-right"
            cursor="nesw-resize"
            onMouseDown={e => handleCornerMouseDown('top-right', e)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDraggingCorner('top-right');
            }}
          />
          <Styles.CornerHandle
            position="bottom-left"
            cursor="nesw-resize"
            onMouseDown={e => handleCornerMouseDown('bottom-left', e)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDraggingCorner('bottom-left');
            }}
          />
          <Styles.CornerHandle
            position="bottom-right"
            cursor="nwse-resize"
            onMouseDown={e => handleCornerMouseDown('bottom-right', e)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDraggingCorner('bottom-right');
            }}
          />
        </Styles.CropCircle>
      </Styles.CropContainer>

      <Styles.Actions>
        <Button
          label={locale.cancel}
          variant="secondary"
          onClick={onCancel}
          disabled={isSaving}
        />
        <Button
          label={locale.save}
          onClick={handleConfirm}
          loading={isSaving}
          disabled={isSaving}
        />
      </Styles.Actions>
    </Styles.Container>
  );
};

export default ImageCropper;
